import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { SignatureModalValue } from '../components/signature/SignatureModal';
import { getSupabaseClient } from './supabase';

type ClientOption = { supabase?: SupabaseClient };

export type SignatureRecordStatus = 'active' | 'voided';

export type SignatureRecord = {
  signature_record_id: string;
  target_type: string;
  target_id: string;
  target_label: string | null;
  event_id: string | null;
  signature_purpose: string;
  signer_profile_id: string | null;
  signer_email: string | null;
  signer_name: string | null;
  signed_at: string;
  signature_method: 'click' | 'typed_name' | 'e_signature';
  signature_data: Record<string, unknown>;
  signature_image_data_url: string | null;
  signature_image_mime_type: string | null;
  acceptance_statement: string;
  signed_payload: Record<string, unknown>;
  source: string;
  ip_address: string | null;
  user_agent: string | null;
  status: SignatureRecordStatus;
  record_certificate: Record<string, unknown>;
  record_hash: string | null;
  signature_hash: string | null;
  certificate_version: string;
  related_table: string | null;
  related_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  voided_at: string | null;
  voided_by: string | null;
  void_reason: string | null;
};

export type SignatureRecordEvent = {
  signature_record_event_id: string;
  signature_record_id: string;
  target_type: string | null;
  target_id: string | null;
  action: 'created' | 'updated' | 'voided' | 'deleted';
  changed_at: string;
  changed_by: string | null;
  old_status: string | null;
  new_status: string | null;
  old_record_hash: string | null;
  new_record_hash: string | null;
  reason: string | null;
  metadata: Record<string, unknown>;
};

export type SignatureTarget = {
  type: string;
  id: string;
  label?: string | null;
  eventId?: string | null;
};

export type SignatureRecordRelation = {
  table: string;
  id: string;
};

export type CreateSignatureRecordParams = {
  target: SignatureTarget;
  purpose: string;
  acceptanceStatement: string;
  signature: SignatureModalValue;
  signedPayload?: Record<string, unknown>;
  signerProfileId?: string | null;
  signerEmail?: string | null;
  signerName?: string | null;
  /**
   * True when the signer has no ITA profile — e.g. a customer signing in person
   * on a staff device. Without it the RPC falls back to the acting user's
   * profile, which would attribute the customer's signature to that staff
   * member. `created_by` still records who captured it.
   */
  signerIsExternal?: boolean;
  source?: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  related?: SignatureRecordRelation | null;
  metadata?: Record<string, unknown>;
};

export type CreateSignatureRecordRpcParams = {
  p_target_type: string;
  p_target_id: string;
  p_signature_purpose: string;
  p_signature_method: SignatureModalValue['method'];
  p_signature_data: Record<string, unknown>;
  p_acceptance_statement: string;
  p_signed_payload: Record<string, unknown>;
  p_target_label: string | null;
  p_event_id: string | null;
  p_signer_profile_id: string | null;
  p_signer_email: string | null;
  p_signer_name: string | null;
  p_signed_at: string;
  p_signature_image_data_url: string | null;
  p_signature_image_mime_type: string | null;
  p_source: string;
  p_ip_address: string | null;
  p_user_agent: string | null;
  p_related_table: string | null;
  p_related_id: string | null;
  p_metadata: Record<string, unknown>;
  /** True when the signer has no ITA profile (e.g. a customer signing in person). */
  p_signer_is_external: boolean;
};

const SIGNATURE_RECORD_COLUMNS = [
  'signature_record_id',
  'target_type',
  'target_id',
  'target_label',
  'event_id',
  'signature_purpose',
  'signer_profile_id',
  'signer_email',
  'signer_name',
  'signed_at',
  'signature_method',
  'signature_data',
  'signature_image_data_url',
  'signature_image_mime_type',
  'acceptance_statement',
  'signed_payload',
  'source',
  'ip_address',
  'user_agent',
  'status',
  'record_certificate',
  'record_hash',
  'signature_hash',
  'certificate_version',
  'related_table',
  'related_id',
  'metadata',
  'created_at',
  'created_by',
  'updated_at',
  'updated_by',
  'deleted_at',
  'deleted_by',
  'voided_at',
  'voided_by',
  'void_reason',
].join(',');

const SIGNATURE_RECORD_EVENT_COLUMNS = [
  'signature_record_event_id',
  'signature_record_id',
  'target_type',
  'target_id',
  'action',
  'changed_at',
  'changed_by',
  'old_status',
  'new_status',
  'old_record_hash',
  'new_record_hash',
  'reason',
  'metadata',
].join(',');

export function buildSignatureRecordRpcParams(params: CreateSignatureRecordParams): CreateSignatureRecordRpcParams {
  const imageDataUrl = params.signature.imageDataUrl
    ?? (typeof params.signature.signatureData.drawn_signature === 'string'
      ? params.signature.signatureData.drawn_signature
      : null);
  const imageMimeType = params.signature.imageMimeType
    ?? (typeof params.signature.signatureData.drawn_signature_mime_type === 'string'
      ? params.signature.signatureData.drawn_signature_mime_type
      : null);

  return {
    p_target_type: params.target.type,
    p_target_id: params.target.id,
    p_signature_purpose: params.purpose,
    p_signature_method: params.signature.method,
    p_signature_data: params.signature.signatureData,
    p_acceptance_statement: params.acceptanceStatement,
    p_signed_payload: params.signedPayload ?? {},
    p_target_label: params.target.label ?? null,
    p_event_id: params.target.eventId ?? null,
    p_signer_profile_id: params.signerProfileId ?? null,
    p_signer_email: params.signerEmail ?? null,
    p_signer_name: params.signerName ?? params.signature.signerName,
    p_signed_at: params.signature.signedAt,
    p_signature_image_data_url: imageDataUrl,
    p_signature_image_mime_type: imageMimeType,
    p_source: params.source ?? 'webapp',
    p_ip_address: params.ipAddress ?? null,
    p_user_agent: params.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : null),
    p_related_table: params.related?.table ?? null,
    p_related_id: params.related?.id ?? null,
    p_metadata: params.metadata ?? {},
    p_signer_is_external: params.signerIsExternal === true,
  };
}

export async function createSignatureRecord(
  params: CreateSignatureRecordParams,
  options?: ClientOption,
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('create_signature_record', buildSignatureRecordRpcParams(params));
  if (error) throw error;
  return String(data ?? '');
}

export async function listSignatureRecordsForTarget(
  target: Pick<SignatureTarget, 'type' | 'id'>,
  options?: ClientOption,
): Promise<{ data: SignatureRecord[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('signature_records')
    .select(SIGNATURE_RECORD_COLUMNS)
    .eq('target_type', target.type)
    .eq('target_id', target.id)
    .order('signed_at', { ascending: false });

  return {
    data: Array.isArray(data) ? (data as unknown as SignatureRecord[]) : [],
    error: error ?? null,
  };
}

export async function listSignatureRecordEvents(
  signatureRecordId: string,
  options?: ClientOption,
): Promise<{ data: SignatureRecordEvent[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('signature_record_events')
    .select(SIGNATURE_RECORD_EVENT_COLUMNS)
    .eq('signature_record_id', signatureRecordId)
    .order('changed_at', { ascending: false });

  return {
    data: Array.isArray(data) ? (data as unknown as SignatureRecordEvent[]) : [],
    error: error ?? null,
  };
}

export async function voidSignatureRecord(
  signatureRecordId: string,
  reason?: string | null,
  options?: ClientOption,
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('void_signature_record', {
    p_signature_record_id: signatureRecordId,
    p_reason: reason ?? null,
  });
  if (error) throw error;
  return String(data ?? '');
}
